import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../modules/auth/context/AuthContext';
import { desktopNotify, isDesktopApp, onDesktopNotificationActivated, setDesktopBadge } from '../../utils/desktopNotify';
import { browserNotify } from '../../utils/browserNotify';
import { closePushNotifications } from '../../utils/pushNotify';
import { toastForCallEvent } from './callNotifications';
import { toastForPromEvent } from './promNotifications';
import { toastForSiteLeadEvent } from './siteLeadNotifications';
import { toastForInquiryEvent } from './inquiryNotifications';
import useSseEvents from '../../hooks/useSseEvents';
import { telephonyEventsUrl, getTelephonyStatus } from '../../modules/telephony/telephonyService';
import IncomingCallStack from './IncomingCallStack';

// Скільки тримати картку після завершення розмови, якщо оператор її не закрив
const AUTO_HIDE_AFTER_END_MS = 5 * 60 * 1000;
// Дзвінок, який відповів інший / пропущений групою — ховаємо швидко
const AUTO_HIDE_OTHERS_MS = 8 * 1000;
// Сповіщення (замовлення prom, звернення) — тримаємо, поки не закриють, але не довше і не більше N
const NOTICE_TTL_MS = 10 * 60 * 1000;
const NOTICE_MAX = 5;

// Той самий набір, що й canEvents у back/src/routes/telephony.router.js
const EVENT_PERMISSIONS = ['receive_calls', 'view_prom_orders', 'view_site_leads', 'view_inquiries', 'view_inquiry_detail', 'edit_inquiry'];

const NOTICES_STORAGE_KEY = 'spark.notices.v1';
const readSavedNotices = (userId) => {
  try {
    const data = JSON.parse(sessionStorage.getItem(NOTICES_STORAGE_KEY) || 'null');
    if (!data || data.userId !== userId || !Array.isArray(data.notices)) return [];
    return data.notices.filter((n) => n && n.key && Number.isFinite(n.shownAt));
  } catch {
    return [];
  }
};

const IncomingCallContext = createContext({
  calls: [],
  notices: [],
  pbxStatus: null,
  sseConnected: false,
  sseError: null,
  enabled: false,
  dismiss: () => {},
  dismissNotice: () => {},
  setInquiry: () => {},
});

export const useIncomingCalls = () => useContext(IncomingCallContext);

/**
 * Глобальний стан вхідних дзвінків поточного оператора (SSE call.answered /
 * call.ended / pbx.status) і сповіщень (замовлення prom.ua). Рендерить стек карток
 * поверх усіх сторінок. Активний для користувачів з правом telephony.receive_calls
 * (дзвінки), view_prom_orders (замовлення) або доступом до звернень (призначення, черга
 * підрозділу, питання куратору) — сервер сам вирішує, що кому слати.
 */
export default function IncomingCallProvider({ children }) {
  const { user, userCan, isLoading } = useAuth();
  const enabled = Boolean(user) && !isLoading && EVENT_PERMISSIONS.some((key) => userCan('telephony', key));

  const [calls, setCalls] = useState([]); // [{ ...payload, inquiry?, endedLocallyAt? }]
  const [notices, setNotices] = useState([]); // [{ key, title, body, line2, target, tone }]
  const noticeTimers = useRef(new Map());
  const noticesRef = useRef([]);
  noticesRef.current = notices;
  const [pbxStatus, setPbxStatus] = useState(null);
  const hideTimers = useRef(new Map());
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const removeNotice = useCallback((key) => {
    clearTimeout(noticeTimers.current.get(key));
    noticeTimers.current.delete(key);
    setNotices((prev) => prev.filter((n) => n.key !== key));
  }, []);

  // Тости переживають F5: зберігаємо в sessionStorage (разом із часом показу, щоб дотримати TTL)
  const [noticesRestored, setNoticesRestored] = useState(false);
  useEffect(() => {
    if (!enabled || !user?.id || noticesRestored) return;
    const now = Date.now();
    const saved = readSavedNotices(user.id).filter((n) => now - n.shownAt < NOTICE_TTL_MS);
    if (saved.length) {
      setNotices((prev) => [...prev, ...saved.filter((s) => !prev.some((p) => p.key === s.key))].slice(0, NOTICE_MAX));
      saved.forEach((n) => {
        clearTimeout(noticeTimers.current.get(n.key));
        noticeTimers.current.set(n.key, setTimeout(() => removeNotice(n.key), NOTICE_TTL_MS - (now - n.shownAt)));
      });
    }
    setNoticesRestored(true);
  }, [enabled, user?.id, noticesRestored, removeNotice]);
  useEffect(() => {
    if (!noticesRestored || !user?.id) return;
    try { sessionStorage.setItem(NOTICES_STORAGE_KEY, JSON.stringify({ userId: user.id, notices })); } catch { /* sessionStorage недоступний */ }
  }, [notices, noticesRestored, user?.id]);

  // Об'єкт відкрито (з тоста, списку чи push) — системні сповіщення про нього в шторці вже зайві
  useEffect(() => {
    closePushNotifications(pathname);
  }, [pathname]);

  // Desktop (Tauri): дублюємо ключові події системним Windows-тостом — його
  // видно і коли вікно згорнуте в трей. Клік по тосту піднімає вікно (Rust)
  // і веде на дзвінок/звернення.
  useEffect(() => {
    if (!enabled || !isDesktopApp()) return undefined;
    return onDesktopNotificationActivated(({ target }) => {
      if (target) navigate(target);
    });
  }, [enabled, navigate]);

  // Desktop: кружок на іконці (панель задач + трей), поки в блоці сповіщень є тости/дзвінки
  const badgeCount = enabled ? notices.length + calls.length : 0;
  useEffect(() => {
    setDesktopBadge(badgeCount);
  }, [badgeCount]);

  // Web Push: клік по сповіщенню (sw.js) фокусує вкладку і шле { type: 'push-navigate', url } —
  // переходимо роутером, без перезавантаження сторінки.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (e) => {
      if (e.data?.type === 'push-navigate' && typeof e.data.url === 'string' && e.data.url.startsWith('/')) {
        navigate(e.data.url);
        // Відкрили з push — тост про той самий об'єкт у стеку більше не потрібен
        noticesRef.current.filter((n) => n.target === e.data.url).forEach((n) => removeNotice(n.key));
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, removeNotice]);

  const dismiss = useCallback((callId) => {
    clearTimeout(hideTimers.current.get(callId));
    hideTimers.current.delete(callId);
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
  }, []);

  // Закриття користувачем (×, «Відкрити»): прибирає і системну копію в шторці; автозгасання за TTL — ні
  const dismissNotice = useCallback((key) => {
    const target = noticesRef.current.find((n) => n.key === key)?.target;
    removeNotice(key);
    if (target) closePushNotifications(target);
  }, [removeNotice]);

  // In-app тост + системне сповіщення (desktop — Windows-тост, браузер — Notification
  // API, коли вкладка у фоні) з однієї події; найстаріші витісняються
  const notify = useCallback((notice) => {
    if (!notice) return;
    desktopNotify(notice);
    browserNotify(notice, navigate);
    setNotices((prev) => [{ ...notice, shownAt: Date.now() }, ...prev.filter((n) => n.key !== notice.key)].slice(0, NOTICE_MAX));
    clearTimeout(noticeTimers.current.get(notice.key));
    noticeTimers.current.set(notice.key, setTimeout(() => removeNotice(notice.key), NOTICE_TTL_MS));
  }, [removeNotice, navigate]);

  const upsert = useCallback((payload) => {
    setCalls((prev) => {
      const idx = prev.findIndex((c) => c.callId === payload.callId);
      if (idx < 0) return [{ ...payload }, ...prev];
      const next = [...prev];
      next[idx] = { ...prev[idx], ...payload }; // зберігаємо локальні поля (inquiry)
      return next;
    });
  }, []);

  const handlers = useMemo(
    () => ({
      'pbx.status': (data) => setPbxStatus(data),
      // Питання куратору напрямку — бейдж у меню телефонії слухає цю подію вікна
      'inquiry.escalated': (data) => {
        window.dispatchEvent(new CustomEvent('inquiry.escalated', { detail: data }));
        notify(toastForInquiryEvent('inquiry.escalated', data));
      },
      'inquiry.decided': (data) => notify(toastForInquiryEvent('inquiry.decided', data)),
      'inquiry.assigned': (data) => notify(toastForInquiryEvent('inquiry.assigned', data)),
      'inquiry.commented': (data) => notify(toastForInquiryEvent('inquiry.commented', data)),
      'inquiry.curator_notified': (data) => notify(toastForInquiryEvent('inquiry.curator_notified', data)),
      'inquiry.queued': (data) => notify(toastForInquiryEvent('inquiry.queued', data)),
      'inquiry.attention': (data) => notify(toastForInquiryEvent('inquiry.attention', data)),
      'inquiry.auto_shipped': (data) => notify(toastForInquiryEvent('inquiry.auto_shipped', data)),
      'inquiry.invoice_changed': (data) => notify(toastForInquiryEvent('inquiry.invoice_changed', data)),
      'inquiry.control.warning': (data) => notify(toastForInquiryEvent('inquiry.control.warning', data)),
      'inquiry.control.expired': (data) => notify(toastForInquiryEvent('inquiry.control.expired', data)),
      'inquiry.callback.due': (data) => notify(toastForInquiryEvent('inquiry.callback.due', data)),
      'inquiry.callback.result': (data) => notify(toastForInquiryEvent('inquiry.callback.result', data)),
      'call.ringing': (data) => {
        upsert(data);
        desktopNotify(toastForCallEvent('call.ringing', data));
      },
      'call.answered': (data) => upsert(data),
      'prom.order.created': (data) => notify(toastForPromEvent('prom.order.created', data)),
      'prom.order.paid': (data) => notify(toastForPromEvent('prom.order.paid', data)),
      'prom.order.canceled': (data) => notify(toastForPromEvent('prom.order.canceled', data)),
      'site.lead.created': (data) => notify(toastForSiteLeadEvent('site.lead.created', data)),
      'call.ended': (data) => {
        upsert(data);
        desktopNotify(toastForCallEvent('call.ended', data));
        clearTimeout(hideTimers.current.get(data.callId));
        // Відповів інший оператор або пропущений у групі — прибираємо швидко;
        // свою завершену розмову тримаємо, поки оператор не закриє (макс. 5 хв)
        const mine = data.answeredUserId && data.answeredUserId === user?.id;
        hideTimers.current.set(
          data.callId,
          setTimeout(() => dismiss(data.callId), mine ? AUTO_HIDE_AFTER_END_MS : AUTO_HIDE_OTHERS_MS),
        );
      },
    }),
    [upsert, dismiss, notify, user?.id],
  );

  // probe: той самий бекенд звичайним запитом — щоб у підказці була причина (403 право / 401 сесія → редирект / мережа)
  const sseOpts = useMemo(() => ({ probe: getTelephonyStatus }), []);
  const { connected: sseConnected, error: sseError } = useSseEvents(enabled ? telephonyEventsUrl() : null, handlers, sseOpts);

  const setInquiry = useCallback((callId, inquiry) => {
    setCalls((prev) => prev.map((c) => (c.callId === callId ? { ...c, inquiry } : c)));
  }, []);

  useEffect(() => () => {
    for (const t of hideTimers.current.values()) clearTimeout(t);
    for (const t of noticeTimers.current.values()) clearTimeout(t);
  }, []);

  const value = useMemo(
    () => ({ calls, notices, pbxStatus, sseConnected, sseError, enabled, dismiss, dismissNotice, setInquiry }),
    [calls, notices, pbxStatus, sseConnected, sseError, enabled, dismiss, dismissNotice, setInquiry],
  );

  return (
    <IncomingCallContext.Provider value={value}>
      {children}
      {enabled && <IncomingCallStack />}
    </IncomingCallContext.Provider>
  );
}
